import useIntersectionObserver from './useIntersectionObserver';
import ScrambleText from './ScrambleText';
import gastrobot from '../images/gastrobot2.png';
import chatApp from '../images/chatapp-img.png';
import palengke from '../images/palengke.png';
import fourtitudeGarage from '../images/fourtitudeGarage.png';
import bloggaHub from '../images/bloggaHub.png';
import aikaChatbot from '../images/aika-chatbot.png';

const projects = [
  {
    name: 'AIKA - Chatbot',
    label: 'Full-Stack · AI-Chatbot',
    img: aikaChatbot,
    glow: 'proj-glow-teal',
    featured: true,
    desc: 'An AI-powered chatbot that lets users upload PDF/Markdown files as a knowledge base and ask context-aware questions.',
    tech: ['Next.js', 'RAG pipeline', 'Groq LLMs', 'Supabase', 'Hugging Face'],
    github: 'https://github.com/Emmm-07/knowledgebase-ai-chat',
    demo: 'https://aika-chat.vercel.app/',
    isDeployed: true,
  },
  {
    name: 'Chat App',
    label: 'Full-Stack · WebSockets',
    img: chatApp,
    glow: 'proj-glow-green',
    featured: true,
    desc: 'Real-time messaging app built with React, Django Channels, and Redis. JWT authentication, real-time presence, and in-app notifications.',
    tech: ['React', 'Django', 'Redis', 'WebSockets', 'JWT'],
    github: 'https://github.com/Emmm-07/Chat-Application_DjangoxReactJS',
    demo: 'https://em-chat-three.vercel.app/',
    isDeployed: true,
  },
  {
    name: 'Gastrobot',
    label: 'Thesis Project · 2025',
    img: gastrobot,
    glow: 'proj-glow-orange',
    featured: false,
    desc: 'A remote-controlled IoT-enabled robot for insecticide application with live system status and notifications. Features obstacle/gas detection, battery monitoring, and 180° sprayer rotation.',
    tech: ['Arduino Cloud', 'C++', 'React-Native', 'Expo'],
    github: 'https://drive.google.com/drive/folders/1vCloH5djMxUmtEzx_ANcMDn06OndR28T?usp=drive_link',
    demo: 'https://drive.google.com/file/d/1jJxZpXdZqQKHv0zcr_PDp_3gU95EZN-5/view?usp=drive_link',
    isDeployed: false,
  },
  {
    name: 'Waypoint Navigation',
    label: 'Web App · Kiosk',
    img: palengke,
    glow: 'proj-glow-blue',
    featured: false,
    desc: 'A public market platform where customers can locate specific products and get kiosk-based navigation routes to find vendors in the market.',
    tech: ['HTML', 'CSS', 'Javascript', 'Python', 'Django', 'OpenCV', 'SQLite'],
    github: 'https://github.com/Emmm-07/PALengke-Sofdes-Project',
    demo: 'https://reccloud.com/u/2m4r3zj',
    isDeployed: false,
  },
  {
    name: 'Fourtitude Garage',
    label: 'Fullstack · POS System',
    img: fourtitudeGarage,
    glow: 'proj-glow-purple',
    featured: false,
    desc: 'A Point of Sale system for automobile shops with car buying, renting, and servicing modules backed by a full database transaction system.',
    tech: ['Python', 'Flask', 'Javascript', 'HTML', 'CSS'],
    github: 'https://github.com/Emmm-07/Fourtitude-Garage',
    demo: 'https://jam.dev/c/0a9914cf-a55f-4e94-b0ff-45009dc0f426',
    isDeployed: false,
  },
  {
    name: 'Blogga Hub',
    label: 'Full-Stack · Blog',
    img: bloggaHub,
    glow: 'proj-glow-teal',
    featured: false,
    desc: 'A web blogging platform to post, edit and delete blogs, view other users\' posts, and leave reactions. JWT authentication for secure access.',
    tech: ['React', 'Django', 'JWT'],
    github: 'https://github.com/Emmm-07/Blog-App-1',
    demo: 'https://my-blog-app-bay.vercel.app/',
    isDeployed: true,
  }
];

const handleTilt = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const x = (e.clientX - rect.left) / rect.width - 0.5;
  const y = (e.clientY - rect.top) / rect.height - 0.5;
  card.style.transform = `perspective(1000px) rotateY(${x * 10}deg) rotateX(${-y * 10}deg) translateY(-4px)`;
  card.style.transition = 'transform 0.1s ease-out, border-color 0.2s, box-shadow 0.2s';
};

const resetTilt = (e) => {
  const card = e.currentTarget;
  card.style.transform = '';
  card.style.transition = 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s, box-shadow 0.2s';
};

const Projects = () => {
  const [ref, isInView] = useIntersectionObserver();

  return (
    <div ref={ref} className={`section-wrap ${isInView ? 'show' : 'hidden'}`} id="projects">
      <div className="section-head">
        <div className="section-eye">What I've built</div>
        <ScrambleText text="Projects" className="section-title" />
        <div className="section-rule" />
      </div>

      <div className="projects-grid">
        {projects.map((proj) => (
          <div
            key={proj.name}
            className={`proj-card${proj.featured ? ' featured' : ''}`}
            onMouseMove={handleTilt}
            onMouseLeave={resetTilt}
          >
            <div className="proj-thumb">
              <div className={`proj-glow ${proj.glow}`} />
              <img src={proj.img} alt={proj.name} />
              <span className="proj-thumb-label">{proj.label}</span>
            </div>
            <div className="proj-body">
              <div className="proj-header">
                <div className="proj-name">{proj.name}</div>
                <div className="proj-links">
                  <a className="proj-link" href={proj.github} target="_blank" rel="noreferrer">
                    GitHub
                  </a>
                  <a className="proj-link" href={proj.demo} target="_blank" rel="noreferrer">
                    {proj.isDeployed ? 'Live' : 'Demo'}
                  </a>
                </div>
              </div>
              <p className="proj-desc">{proj.desc}</p>
              <div className="proj-chips">
                {proj.tech.map((t) => <span key={t} className="proj-chip">{t}</span>)}
              </div>
            </div>
          </div>
        ))}

        <div className="proj-more-card">
          <div className="proj-more-plus">+</div>
          <div className="proj-more-label">More coming soon</div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
